import {
  PayHubCashierContractsEnumsCashierActionType as ActionType,
  PayHubCashierContractsEnumsSubmitPaymentStatus as PaymentStatus,
} from 'store/models';
import { getCurrentQueryParams } from 'utils';

import type { ReservationHoldMessage } from '../api/reservationHold';
import { isAwaitingVerification } from '../api/reservationHold';
import { RESERVATION_ROOT } from '../routes';
import { ReservationCardStatus } from '../types';

/** Hold described by the payload's own two-stage deposit flags. */
export interface PayloadReservationHold {
  /** `user_card_id` of the held deposit — the card the bank statement belongs to. */
  cardId: number | null;
  /** `user_verified` — `true` → card proof upload, `false` → account KYC on the platform. */
  isUserVerified?: boolean;
}

/**
 * Remembers that the current deposit was handed over to the reservation flow.
 *
 * The hold signal is one-shot: the hub delivers it once, the session-state response carries it once.
 * The ordinary deposit pipeline, however, keeps going after the hand-over — the submit response,
 * a 3DS child frame or `Await` may still navigate to `/deposit/deposit-processing` afterwards. Without
 * a memory of the hold that later navigation wins and the player is pulled out of the flow.
 * `ReservationHoldGate` reads this flag to send them back.
 *
 * Module memory rather than storage on purpose: leaving the flow exits the widget, so the flag dies
 * with it, and a reload re-learns the hold from the hub (sent on connect) or session-state.
 */
let isHoldMarked = false;

/** Set only when the hand-over came from the payload flags — the one source in a new window. */
let payloadHold: PayloadReservationHold | null = null;

export const markReservationHold = (hold?: PayloadReservationHold | null): void => {
  isHoldMarked = true;

  if (hold) {
    payloadHold = hold;
  }
};

export const clearReservationHold = (): void => {
  isHoldMarked = false;
  payloadHold = null;
};

export const isReservationHoldMarked = (): boolean => isHoldMarked;

export const getPayloadReservationHold = (): PayloadReservationHold | null => payloadHold;

export const getReservationEntryPath = (): string => `${RESERVATION_ROOT}${getCurrentQueryParams() || ''}`;

/**
 * Single rule for "does this payload send the player to the reservation flow", shared by the hub
 * listener and the session-state response.
 *
 * A verified card still produces a brief `awaiting_verification` while the hold is captured.
 * Nothing is asked of the player there, so the ordinary processing page must stay — showing the hold
 * screen would demand a document that is already on file.
 */
export const shouldHandOverToReservation = (
  payload: ReservationHoldMessage | null | undefined,
  cardStatus: ReservationCardStatus | null,
): boolean => isAwaitingVerification(payload) && cardStatus !== ReservationCardStatus.verified;

const FINAL_STATUSES: string[] = [PaymentStatus.Success, PaymentStatus.Fail];

/** The payment is still pending (`processing` / `await`) — a final success or fail is never a hold. */
const isPendingPayment = ({ action, result_status }: ReservationHoldMessage): boolean =>
  (result_status === PaymentStatus.Processing || action === ActionType.Await) &&
  !FINAL_STATUSES.includes(result_status || '');

/**
 * Alternative rule from the payload's own flags (hub message / session-state), so a status page
 * opened in a new window — no init, no selected card — still reaches the right screen.
 * Applies only while the payment is pending (`processing` / `await`), then:
 *   1. `requires_two_stage_deposit` false → no reservation flow
 *   2. `user_card_kyc_verifyed` true      → no reservation flow
 *   3. `user_verified`                    → picks the screen (see `EntryScreen`)
 *
 * Returns `null` when the flags do not ask for the flow (or were not sent).
 */
export const resolvePayloadReservationHold = (
  payload: ReservationHoldMessage | null | undefined,
): PayloadReservationHold | null =>
  payload &&
  isPendingPayment(payload) &&
  payload.requires_two_stage_deposit === true &&
  payload.user_card_kyc_verifyed === false
    ? { cardId: payload.user_card_id ?? null, isUserVerified: payload.user_verified }
    : null;
